import { html, SP } from '../html.js';
import type { VNode } from '../html.js';
import type { DashboardActions, DashboardState, Screen } from './store.js';
import { HostScreen } from './screens/host.js';
import { OnboardScreen } from './screens/onboard.js';
import { OverviewScreen } from './screens/overview.js';
import { SettingsScreen } from './screens/settings.js';
import { SitesScreen } from './screens/sites.js';
import { TenantScreen } from './screens/tenant.js';
import { TrafficScreen } from './screens/traffic.js';
import { ThemeToggle } from './ui/theme.js';
import { LoginScreen } from './ui/unlock.js';

/**
 * The frame: a header, four destinations plus the operator's, one screen.
 *
 * Unlike the onboarding console — one job, one path, no router — this page is a
 * set of independent reads of live server state, so every screen is addressable.
 * The nav is anchors rather than buttons on purpose: a hash href gives back the
 * browser's own history, middle-click, and "copy link address" for free, and the
 * router in index.ts listens for `hashchange` so all four arrive the same way.
 *
 * ADD A SITE IS AN ACTION, NOT A DESTINATION. Onboarding held a fifth of the
 * permanent navigation to serve something a customer does once per site and an
 * operator does rarely. It keeps its route — a crawl in flight must survive a
 * reload, which is why the job id is in the hash — and it is entered from the
 * button on the right of this strip and from the Sites empty state.
 *
 * The header states which HOST is being operated, because this bundle is served
 * by whichever ClawHarbor it was built into. Beside it is WHO the server says
 * this page is, in the server's own words: "signed in as <email>" for the
 * cookie session `GET /api/auth/me` reported, or "operator" for the holder of
 * the reload secret. The two end differently, and the control says which:
 * "Sign out" posts to `/api/auth/logout` and clears a cookie this page cannot
 * see; "Forget secret" clears one field in memory and touches no server.
 */

interface Tab {
  screen: Screen;
  label: string;
  hash: string;
}

const TABS: readonly Tab[] = [
  { screen: 'overview', label: 'Overview', hash: '#/' },
  { screen: 'sites', label: 'Sites', hash: '#/sites' },
  { screen: 'traffic', label: 'Traffic', hash: '#/traffic' },
  { screen: 'settings', label: 'Settings', hash: '#/settings' },
];

/** The operator's own destination, appended for the reload-secret holder only. */
const HOST_TAB: Tab = { screen: 'host', label: 'This host', hash: '#/host' };

function hostOf(origin: string): string {
  try {
    return new URL(origin).host;
  } catch {
    return origin;
  }
}

/** The chip and its one control, for whichever principal the server named. */
function Who(props: { state: DashboardState; actions: DashboardActions }): VNode | null {
  const { state, actions } = props;
  const p = state.principal;
  if (state.booting) {
    return html`<span class="chip chip--locked"><i class="chip__dot" aria-hidden="true"></i>Checking…</span>`;
  }
  if (!p) {
    return html`<span class="chip chip--locked"><i class="chip__dot" aria-hidden="true"></i>Signed out</span>`;
  }
  if (p.kind === 'operator') {
    return html`
      <span class="chip chip--operator" title="The reload secret is held in memory; every request carries it.">
        <i class="chip__dot" aria-hidden="true"></i>operator
      </span>
      <button
        type="button"
        class="linkish"
        onClick=${() => actions.forget()}
        title="Clears the secret held in memory. Nothing is sent to the server."
      >
        Forget secret
      </button>
    `;
  }
  return html`
    <span class="chip chip--account rail__who" title=${`Signed in as ${p.email}`}>
      <i class="chip__dot" aria-hidden="true"></i>signed in as${SP}<b class="rail__email">${p.email}</b>
    </span>
    <button
      type="button"
      class="linkish"
      onClick=${() => actions.signOut()}
      title="Ends the session on the server and clears the cookie."
    >
      Sign out
    </button>
  `;
}

export function DashboardView(props: {
  state: DashboardState;
  actions: DashboardActions;
  now: number;
  /** The origin every request on this page goes to. Named in the header. */
  origin: string;
}): VNode {
  const { state, actions, now, origin } = props;
  const signedOut = !state.booting && state.principal === null;

  const screen = state.booting
    ? html`<section class="wrap screen">
        <p class="boot2" role="status">Asking ${hostOf(origin)} who you are…</p>
      </section>`
    : signedOut
      ? LoginScreen({ state, actions })
      : state.screen === 'tenant'
        ? TenantScreen({ state, actions, now, origin })
        : state.screen === 'crawl'
          ? OnboardScreen({ state, actions, now })
          : state.screen === 'traffic'
            ? TrafficScreen({ state, actions })
            : state.screen === 'settings'
              ? SettingsScreen({ state, actions, now })
              : state.screen === 'host'
                ? HostScreen({ state, actions, now })
                : state.screen === 'sites'
                  ? SitesScreen({ state, actions, now })
                  : OverviewScreen({ state, actions, now });

  return html`
    <div class="shell">
      <header class="rail">
        <div class="wrap rail__in">
          <span class="mark">
            <span class="mark__flag" aria-hidden="true"></span>ClawHarbor
          </span>
          <span class="rail__role">Dashboard</span>
          <span class="rail__spacer"></span>
          <span class="rail__host" title="Every request on this page goes to this host.">
            ${hostOf(origin)}
          </span>
          ${signedOut || state.booting ? ThemeToggle({ theme: state.theme, actions }) : null}
          ${Who({ state, actions })}
        </div>

        ${signedOut || state.booting
          ? null
          : html`<nav class="tabs" aria-label="Dashboard sections">
              <div class="wrap tabs__in">
                ${[...TABS, ...(state.principal?.kind === 'operator' ? [HOST_TAB] : [])].map((tab) => {
                  // A site is reached from the Sites table and carries its own
                  // second-level strip, so Sites stays the current section while
                  // one is open — otherwise the nav claims you are nowhere. The
                  // crawl screen is the same case for the button beside them.
                  const current =
                    tab.screen === state.screen ||
                    (tab.screen === 'sites' && state.screen === 'tenant');
                  return html`<a
                    class=${current ? 'tab tab--on' : 'tab'}
                    href=${tab.hash}
                    aria-current=${current ? 'page' : undefined}
                    >${tab.label}</a
                  >`;
                })}
                <span class="tabs__spacer"></span>
                <a
                  class=${state.screen === 'crawl' ? 'btn btn--sm btn--ghost' : 'btn btn--sm'}
                  href="#/crawl"
                  aria-current=${state.screen === 'crawl' ? 'page' : undefined}
                  >Add a site</a
                >
              </div>
            </nav>`}
      </header>

      ${state.notice
        ? html`<div class="wrap">
            <p class="notice" role="status">
              ${state.notice}
              <button type="button" class="notice__x" onClick=${() => actions.dismissNotice()}>
                Dismiss
              </button>
            </p>
          </div>`
        : null}

      <main class="main">${screen}</main>

      <footer class="wrap foot">
        Every number on these screens came out of a response from${SP}
        <code>${hostOf(origin)}</code>; nothing is estimated, averaged into existence, or replayed
        from a fixture. Calls are counters kept by the usage store; the Settings screen says whether
        this host has a card processor behind them, and until it does, nothing here bills anybody.
        Published tenants serve read-only tools: no tool can write, book, pay or authenticate, and
        the strongest kind is <code>stage</code>, which returns a deep link a person has to confirm.
      </footer>
    </div>
  `;
}
